/* src/Pages/SlotsPage/index.tsx */
import React, { useEffect, useState, useCallback, useMemo, useRef, startTransition } from 'react';
import { Card, Space, message, Spin, Progress, Alert, Typography } from 'antd';
import type { InputRef } from 'antd';

const { Text } = Typography;
import type { Key } from 'react';

import {
  createSlot,
  updateSlot,
  batchExtend,
  batchRefund,
  fetchAllSlots,
  deleteSlot,
} from '../../Modules/Slot/Presentation/actions';

import { CreateSlotDTO, Slot, SlotSearchParams } from '../../Modules/Slot/Model/Slot';
import SlotTable from './components/SlotTable';
import ManagementBar from './components/ManagementBar';
import SlotAddModal from './components/SlotAddModal';
import SlotExtendModal from './components/SlotExtendModal';
import SlotEditModal from './components/SlotEditModal';
import SlotRefundModal from './components/SlotRefundModal';
import ExcelImportModal from './components/ExcelImportModal';
import ExcelBatchUpdateModal from './components/ExcelBatchUpdateModal';
import SlotOpsOverrideBanner from './components/SlotOpsOverrideBanner';


import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';
import { useSlots } from '../../Modules/Slot/Presentation/hooks/hooks';
import { selectUser } from '../../Modules/User/Presentation/selectors';
import { selectAccounts } from '../../Modules/Account/Presentation/selectors';
import { 
  selectDefaultPageSlots, 
  selectDefaultPagePagination, 
  selectDefaultPageLoaded 
} from '../../Modules/Slot/Presentation/selectors';
import {
  setDefaultPage,
  optimisticUpdateSlot,
  optimisticBatchExtend,
  optimisticBatchRefund,
  optimisticAddSlots,
  optimisticDeleteSlots,
  removeOptimisticSlots,
  rollbackSlots,
  markSlotsRankPending,
  applyRankPollResults,
} from '../../Modules/Slot/Presentation/reducers';
import { fetchAccounts } from '../../Modules/Account/Presentation/actions';
import useOnce from '../../Utils/hook/useOnce';
import SlotBulkEditModal from './components/SlotBulkEditModal';
import { getAccountIdById } from '../../Utils/getAccountNameById';
import { assertAdminOrManager } from '../../Modules/Slot/Domain/UseCase/Utils/Permission';
import { batchCreateSlots } from '../../Modules/Slot/Presentation/actions';
import {
  buildOptimisticSlot,
  buildOptimisticSlotsFromRequest,
  createTempSlotId,
} from '../../Utils/optimisticSlot';
import {
  BATCH_CREATE_CHUNK_DELAY_MS,
  BATCH_CREATE_CHUNK_SIZE,
  splitBatchCreateIntoChunks,
} from '../../Utils/batchCreateChunks';
import { SlotRepositoryImpl } from '../../Modules/Slot/Data/Repositories/SlotRepositoryImpl';
import {
  dtoTriggersAsyncRank,
  pollSlotRanks,
  shouldKeepRankPending,
} from '../../Utils/slotRankPolling';
import { isAdpa99Username } from '../../Utils/usernameSuffix';

const slotRankRepo = new SlotRepositoryImpl();

const SlotsPage: React.FC = () => {

  /** ---------------------------------------------------------------- */
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectUser);   // 아직 null 일 수 있음
  const accounts = useAppSelector(selectAccounts);
  
  // 컴포넌트 최상단 ref
  const topRef = useRef<HTMLDivElement>(null);
  const rankPollControllersRef = useRef<Set<AbortController>>(new Set());

  useEffect(() => {
    return () => {
      rankPollControllersRef.current.forEach((controller) => controller.abort());
      rankPollControllersRef.current.clear();
    };
  }, []);

  const startRankPolling = useCallback((slotIds: number[]) => {
    const ids = Array.from(new Set(slotIds.filter((id) => id > 0)));
    if (ids.length === 0) return;

    const controller = new AbortController();
    rankPollControllersRef.current.add(controller);

    void pollSlotRanks(
      (chunkIds) => slotRankRepo.getBatchDetail(chunkIds),
      ids,
      {
        signal: controller.signal,
        onUpdate: (slots) => {
          dispatch(applyRankPollResults({ slots }));
        },
        onFinish: (unclearedIds) => {
          if (unclearedIds.length > 0) {
            dispatch(applyRankPollResults({ clearPendingIds: unclearedIds }));
          }
        },
      },
    )
      .catch(() => {
        dispatch(applyRankPollResults({ clearPendingIds: ids }));
      })
      .finally(() => {
        rankPollControllersRef.current.delete(controller);
      });
  }, [dispatch]);

  const beginAsyncRankWatch = useCallback((slotIds: number[], options?: { invalidateRank?: boolean }) => {
    const ids = Array.from(new Set(slotIds.filter((id) => id > 0)));
    if (ids.length === 0) return;
    dispatch(markSlotsRankPending({ ids, invalidateRank: options?.invalidateRank }));
    startRankPolling(ids);
  }, [dispatch, startRankPolling]);

  /* ==============  검색/필터 상태 ============== */
  const [searchParams, setSearchParams] = useState<SlotSearchParams>({
    page: 1,
    pageSize: 20,
    active: 'true',
    order_by: 'registered',
    order_dir: 'desc'
  });

  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState(''); // 실제 검색에 사용될 값
  const [searchField, setSearchField] = useState<'slot' | 'keyword' | 'url' | 'user' | 'creator' | 'mid_single' | 'mid_compare'>('slot');

  // 검색 유형만 바꿀 때는 목록을 다시 불러오지 않음 (검색 버튼/Enter 시에만 반영)
  const handleSearchFieldChange = useCallback((field: 'slot' | 'keyword' | 'url' | 'user' | 'creator' | 'mid_single' | 'mid_compare') => {
    setSearchField(field);
    setSearch('');
  }, []);

  /* ==============  UI 상태 ============== */
  const [selected, setSelected] = useState<Key[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [extOpen, setExtOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [excelUploading, setExcelUploading] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [excelImportFile, setExcelImportFile] = useState<File | null>(null);
  const [excelBatchUpdateOpen, setExcelBatchUpdateOpen] = useState(false);
  const [excelBatchUpdateFile, setExcelBatchUpdateFile] = useState<File | null>(null);
  const [localFindOpen, setLocalFindOpen] = useState(false);
  const [localFindQuery, setLocalFindQuery] = useState('');
  const [localFindMatchCount, setLocalFindMatchCount] = useState(0);
  const [localFindActiveMatchIndex, setLocalFindActiveMatchIndex] = useState(-1);
  const [localFindFocusNonce, setLocalFindFocusNonce] = useState(0);
  const localFindInputRef = useRef<InputRef>(null);

  /* ==============  분할 업로드 상태 ============== */
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [batchUploadTotal, setBatchUploadTotal] = useState(0);
  const [batchUploadCurrent, setBatchUploadCurrent] = useState(0);

  /* ==============  새로고침 방지  ============== */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isBatchUploading) {
        e.preventDefault();
        e.returnValue = ''; // 크롬에서 경고창을 띄우기 위해 필요
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isBatchUploading]);

  /** me 가 바뀔 때마다 슬롯 훅 */
  const { slots, loading, error, pagination, filters, refresh } = useSlots(me!, searchParams);

  // 기본 페이지 캐시
  const defaultPageSlots = useAppSelector(selectDefaultPageSlots);
  const defaultPagePagination = useAppSelector(selectDefaultPagePagination);
  const defaultPageLoaded = useAppSelector(selectDefaultPageLoaded);

  // 기본 페이지인지 확인 (검색 조건이 없고 1페이지인 경우)
  const isDefaultPage = useMemo(() => 
    !searchValue.trim() && 
    (!searchParams.slotId && !searchParams.keyword && !searchParams.sellerId && 
     !searchParams.creatorId && !searchParams.url && !searchParams.mid_single && !searchParams.mid_compare) && 
    searchParams.page === 1 &&
    searchParams.pageSize === 20 &&
    // 정렬이 기본값(등록순 desc)일 때만 기본 페이지로 간주
    ((searchParams.order_by === undefined || searchParams.order_by === 'registered') &&
     (searchParams.order_dir === undefined || searchParams.order_dir === 'desc')),
    [searchValue, searchParams]
  );

  // 기본 페이지 데이터를 캐시에 저장
  useEffect(() => {
    if (isDefaultPage && slots.length > 0 && !defaultPageLoaded) {
      dispatch(setDefaultPage({ slots, pagination }));
    }
  }, [isDefaultPage, slots, pagination, defaultPageLoaded, dispatch]);

  // 기본 페이지일 때 캐시된 데이터 사용 (단, needsRefresh가 true이면 캐시 무시)
  const needsRefresh = useAppSelector((state) => state.slot.needsRefresh);
  const displaySlots = useMemo(() => {
    const useCache = isDefaultPage && defaultPageLoaded && !needsRefresh;
    const raw = useCache ? defaultPageSlots : slots;
    const seen = new Set<number>();
    const deduped: typeof raw = [];
    for (const s of raw) {
      if (seen.has(s.slotId)) continue;
      seen.add(s.slotId);
      deduped.push(s);
    }
    return deduped;
  }, [isDefaultPage, defaultPageLoaded, defaultPageSlots, slots, needsRefresh]);
  
  const displayPagination = useMemo(() => 
    isDefaultPage && defaultPageLoaded && !needsRefresh ? defaultPagePagination : pagination,
    [isDefaultPage, defaultPageLoaded, defaultPagePagination, pagination, needsRefresh]
  );

  // UI 즉시 반영을 위해 페이지/사이즈는 로컬 상태(searchParams)로 제어
  const effectivePagination = useMemo(() => {
    if (!displayPagination) return undefined;
    return {
      ...displayPagination,
      current_page: searchParams.page ?? displayPagination.current_page,
      page_size: searchParams.pageSize ?? displayPagination.page_size,
    };
  }, [displayPagination, searchParams.page, searchParams.pageSize]);

  /* ➜ 최초 한 번: 계정 목록, 슬롯 목록 */
  useOnce(() => { if (accounts.length === 0) dispatch(fetchAccounts()); });
  useOnce(() => { refresh(); });

  /* ==============  검색 파라미터 업데이트 ============== */
  const updateSearchParams = useCallback((newParams: Partial<SlotSearchParams>) => {
    setSearchParams(prev => ({
      ...prev,
      ...newParams,
      page: 1 // 검색 조건이 변경되면 첫 페이지로
    }));
  }, []);

  const openLocalFind = useCallback(() => {
    setLocalFindOpen(true);
    setLocalFindFocusNonce(prev => prev + 1);
  }, []);

  const closeLocalFind = useCallback(() => {
    setLocalFindOpen(false);
    setLocalFindQuery('');
    setLocalFindMatchCount(0);
    setLocalFindActiveMatchIndex(-1);
  }, []);

  const handleLocalFindQueryChange = useCallback((value: string) => {
    setLocalFindQuery(value);
    setLocalFindActiveMatchIndex(value.trim() ? 0 : -1);
  }, []);

  const handleLocalFindMatchCountChange = useCallback((count: number) => {
    setLocalFindMatchCount(count);
    setLocalFindActiveMatchIndex(prev => {
      if (count === 0) return -1;
      if (prev < 0) return 0;
      return prev >= count ? count - 1 : prev;
    });
  }, []);

  const handleLocalFindNext = useCallback(() => {
    if (!localFindQuery.trim() || localFindMatchCount === 0) return;
    setLocalFindActiveMatchIndex(prev => (prev < 0 ? 0 : (prev + 1) % localFindMatchCount));
  }, [localFindMatchCount, localFindQuery]);

  const handleLocalFindPrev = useCallback(() => {
    if (!localFindQuery.trim() || localFindMatchCount === 0) return;
    setLocalFindActiveMatchIndex(prev => (
      prev < 0 ? 0 : (prev - 1 + localFindMatchCount) % localFindMatchCount
    ));
  }, [localFindMatchCount, localFindQuery]);

  const restoreLocalFindInputFocus = useCallback(() => {
    if (!localFindOpen) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        localFindInputRef.current?.focus({ cursor: 'end' });
      });
    });
  }, [localFindOpen]);

  useEffect(() => {
    if (!localFindOpen) return;
    const timer = window.setTimeout(() => {
      localFindInputRef.current?.focus({ cursor: 'all' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [localFindOpen, localFindFocusNonce]);

  useEffect(() => {
    const handleLocalFindShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        openLocalFind();
      }
    };

    window.addEventListener('keydown', handleLocalFindShortcut);
    return () => window.removeEventListener('keydown', handleLocalFindShortcut);
  }, [openLocalFind]);

  // 검색 실행 (allowClear 클릭 시 빈 값으로 onSearch가 호출되므로, 전체 목록 리셋은 검색 버튼/Enter일 때만)
  const handleSearch = useCallback((
    value: string,
    _event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLInputElement>,
    info?: { source?: 'clear' | 'input' },
  ) => {
    if (!value.trim() && info?.source === 'clear') {
      return;
    }
    setSearchValue(value);
    
    const params: Partial<SlotSearchParams> = {};
    
    // 검색어가 있을 때만 검색 조건 추가
    if (value.trim()) {
      switch (searchField) {
        case 'slot':
          const rawValue = value.trim();
          if (/^\d+$/.test(rawValue)) {
            params.slotId = parseInt(rawValue);
          } else {
            message.error('올바르지 않은 슬롯 번호 형식입니다. 숫자만 입력해주세요.');
            return;
          }
          break;
        case 'keyword':
          params.keyword = value.trim();
          break;
        case 'url':
          params.url = value.trim();
          break;
        case 'user':
          {
            const searchLower = value.trim().toLowerCase();
            const sellersOnly = accounts.filter(acc => acc.grade === 'SELLER');

            // 1) 정확 일치 (username) - 최우선
            const exactUsername = sellersOnly.find(acc => acc.username.toLowerCase() === searchLower);
            if (exactUsername) { 
              params.sellerId = exactUsername.id; 
              break; 
            }

            // 2) 숫자인 경우 ID로 검색
            const sellerId = parseInt(value.trim());
            // 숫자로만 이루어진 경우에만 ID 검색을 시도하거나, 
            // parseInt 결과가 원래 문자열과 숫자적으로 동일한지 확인하는 것이 안전하지만
            // 여기서는 일단 기존 로직의 흐름을 유지하되 정확한 username 매칭을 위로 올림
            if (!isNaN(sellerId) && /^\d+$/.test(value.trim())) {
              const targetAcc = accounts.find(acc => acc.id === sellerId);
              if (targetAcc && targetAcc.grade === 'SELLER') {
                params.sellerId = sellerId;
                break;
              }
            }

            // 3) 정확 일치 (name)
            const exactName = sellersOnly.find(acc => acc.name.toLowerCase() === searchLower);
            if (exactName) { params.sellerId = exactName.id; break; }

            // 4) prefix 일치 (username)
            const startsUser = sellersOnly.filter(acc => acc.username.toLowerCase().startsWith(searchLower));
            if (startsUser.length > 0) {
              startsUser.sort((a, b) => a.username.length - b.username.length || a.id - b.id);
              params.sellerId = startsUser[0].id; break;
            }

            // 5) prefix 일치 (name)
            const startsName = sellersOnly.filter(acc => acc.name.toLowerCase().startsWith(searchLower));
            if (startsName.length > 0) {
              startsName.sort((a, b) => a.name.length - b.name.length || a.id - b.id);
              params.sellerId = startsName[0].id; break;
            }

            // 6) 부분 일치 (username)
            const includesUser = sellersOnly.filter(acc => acc.username.toLowerCase().includes(searchLower));
            if (includesUser.length > 0) {
              includesUser.sort((a, b) => {
                const ia = a.username.toLowerCase().indexOf(searchLower);
                const ib = b.username.toLowerCase().indexOf(searchLower);
                if (ia !== ib) return ia - ib; // 앞쪽 매치 우선
                return a.username.length - b.username.length || a.id - b.id;
              });
              params.sellerId = includesUser[0].id; break;
            }

            // 7) 부분 일치 (name)
            const includesName = sellersOnly.filter(acc => acc.name.toLowerCase().includes(searchLower));
            if (includesName.length > 0) {
              includesName.sort((a, b) => {
                const ia = a.name.toLowerCase().indexOf(searchLower);
                const ib = b.name.toLowerCase().indexOf(searchLower);
                if (ia !== ib) return ia - ib;
                return a.name.length - b.name.length || a.id - b.id;
              });
              params.sellerId = includesName[0].id; break;
            }

            // 매치가 하나도 없으면 검색하지 않음
            message.error('존재하지 않는 셀러입니다.');
            return;
          }
        case 'creator':
          {
            const searchLower = value.trim().toLowerCase();
            const allAccounts = accounts;

            // 1) 정확 일치 (username) - 최우선
            const exactUsername = allAccounts.find(acc => acc.username.toLowerCase() === searchLower);
            if (exactUsername) { 
              params.creatorId = exactUsername.id; 
              break; 
            }

            // 2) 숫자인 경우 ID로 검색
            const creatorId = parseInt(value.trim());
            if (!isNaN(creatorId) && /^\d+$/.test(value.trim())) {
              const targetAcc = accounts.find(acc => acc.id === creatorId);
              if (targetAcc) {
                params.creatorId = creatorId;
                break;
              }
            }

            // 3) 정확 일치 (name)
            const exactName = allAccounts.find(acc => acc.name.toLowerCase() === searchLower);
            if (exactName) { params.creatorId = exactName.id; break; }

            // 4) prefix 일치 (username)
            const startsUser = allAccounts.filter(acc => acc.username.toLowerCase().startsWith(searchLower));
            if (startsUser.length > 0) {
              startsUser.sort((a, b) => a.username.length - b.username.length || a.id - b.id);
              params.creatorId = startsUser[0].id; break;
            }

            // 5) prefix 일치 (name)
            const startsName = allAccounts.filter(acc => acc.name.toLowerCase().startsWith(searchLower));
            if (startsName.length > 0) {
              startsName.sort((a, b) => a.name.length - b.name.length || a.id - b.id);
              params.creatorId = startsName[0].id; break;
            }

            // 6) 부분 일치 (username)
            const includesUser = allAccounts.filter(acc => acc.username.toLowerCase().includes(searchLower));
            if (includesUser.length > 0) {
              includesUser.sort((a, b) => {
                const ia = a.username.toLowerCase().indexOf(searchLower);
                const ib = b.username.toLowerCase().indexOf(searchLower);
                if (ia !== ib) return ia - ib; // 앞쪽 매치 우선
                return a.username.length - b.username.length || a.id - b.id;
              });
              params.creatorId = includesUser[0].id; break;
            }

            // 7) 부분 일치 (name)
            const includesName = allAccounts.filter(acc => acc.name.toLowerCase().includes(searchLower));
            if (includesName.length > 0) {
              includesName.sort((a, b) => {
                const ia = a.name.toLowerCase().indexOf(searchLower);
                const ib = b.name.toLowerCase().indexOf(searchLower);
                if (ia !== ib) return ia - ib;
                return a.name.length - b.name.length || a.id - b.id;
              });
              params.creatorId = includesName[0].id; break;
            }

            // 매치가 하나도 없으면 검색하지 않음
            message.error('존재하지 않는 생성자입니다.');
            return;
          }
        case 'mid_single':
          // MID 검색 처리
          params.mid_single = value.trim();
          break;
        case 'mid_compare':
          // MID 검색 처리
          params.mid_compare = value.trim();
          break;
      }
    }

    // 항상 활성 상태만 조회
    params.active = 'true';
    
    // 검색어가 비어있으면 검색 파라미터 초기화하되 페이지 크기는 유지
    if (!value.trim()) {
      setSearchParams(prev => ({
        page: 1,
        pageSize: prev.pageSize, // 현재 페이지 크기 유지
        active: 'true',
        order_by: prev.order_by ?? 'registered',
        order_dir: prev.order_dir ?? 'desc'
      }));
    } else {
      // 이전 검색 조건 키가 섞이지 않도록 병합 없이 덮어씀
      setSearchParams(prev => ({
        page: 1,
        pageSize: prev.pageSize,
        active: 'true',
        order_by: prev.order_by ?? 'registered',
        order_dir: prev.order_dir ?? 'desc',
        ...params,
      }));

      if (
        params.slotId === searchParams.slotId &&
        params.keyword === searchParams.keyword &&
        params.url === searchParams.url &&
        params.sellerId === searchParams.sellerId &&
        params.creatorId === searchParams.creatorId &&
        params.mid_single === searchParams.mid_single &&
        params.mid_compare === searchParams.mid_compare &&
        searchParams.page === 1
      ) {
        refresh();
      }
    }
  }, [searchField, accounts, searchParams, refresh]);

   // 페이지 변경
   const handlePageChange = useCallback((page: number, pageSize?: number) => {
    setSearchParams(prev => ({
      ...prev,
      page,
      pageSize: pageSize ?? prev.pageSize
    }));
  }, []);

  // 페이지 변경 시 화면을 위로 이동
  useEffect(() => {
    if (searchParams.page && searchParams.page > 1) {
      // ref를 사용하여 최상단으로 스크롤
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [searchParams.page]);

  /* ==============  선택 슬롯 정보 ============== */
  const selectedIdSet = useMemo(() => new Set(selected as number[]), [selected]);
  const slotById = useMemo(() => new Map(slots.map((s) => [s.slotId, s] as const)), [slots]);
  const defaultSlotById = useMemo(
    () => new Map(defaultPageSlots.map((s) => [s.slotId, s] as const)),
    [defaultPageSlots]
  );
  const selectedSlots = useMemo(() => 
    Array.from(selectedIdSet).map((id) => slotById.get(id)).filter((v): v is Slot => !!v),
    [selectedIdSet, slotById]
  );
  const handleSelectChange = useCallback((ids: Key[]) => {
    startTransition(() => {
      setSelected(Array.from(new Set(ids)));
    });
  }, []);

  /* ==============  공통 성공 콜백 ============== */
  const handleSuccess = useCallback((msg: string) => {
    message.success(msg);
    setSelected([]);
  }, []);

  const cloneSlot = useCallback((slot?: Slot) => (slot ? { ...slot } : undefined), []);

  /* ==============  CRUD / 배치 ============== */
  const runBackgroundBatchCreate = useCallback(
    async (
      request: { count: number; slots: CreateSlotDTO[] },
      tempIds: number[],
      options?: { successLabel?: string },
    ) => {
      if (!me) return;

      const totalCount = tempIds.length;
      const successLabel = options?.successLabel ?? '슬롯';
      const chunks = splitBatchCreateIntoChunks(request, tempIds);
      const isSplitUpload = totalCount > BATCH_CREATE_CHUNK_SIZE;
      const pollIds: number[] = [];

      if (isSplitUpload) {
        setIsBatchUploading(true);
        setBatchUploadTotal(totalCount);
        setBatchUploadCurrent(0);
      }

      let successCount = 0;
      let failCount = 0;

      try {
        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i];
          try {
            const created = await dispatch(
              batchCreateSlots({
                request: chunk.request,
                me,
                tempIds: chunk.tempIds,
                optimisticApplied: true,
              }),
            ).unwrap();
            successCount += chunk.tempIds.length;
            if (Array.isArray(created)) {
              created.forEach((slot) => {
                if (shouldKeepRankPending(slot)) pollIds.push(slot.slotId);
              });
            }
            if (isSplitUpload) setBatchUploadCurrent(successCount);
            if (i < chunks.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, BATCH_CREATE_CHUNK_DELAY_MS));
            }
          } catch {
            failCount += chunk.tempIds.length;
            dispatch(removeOptimisticSlots({ tempIds: chunk.tempIds }));
            if (isSplitUpload) setBatchUploadCurrent(successCount + failCount);
          }
        }
      } finally {
        if (isSplitUpload) setIsBatchUploading(false);
        if (pollIds.length > 0) startRankPolling(pollIds);
        if (failCount === 0) {
          message.success(`총 ${successCount}개 ${successLabel} 생성이 완료되었습니다.`);
        } else if (successCount > 0) {
          message.warning(`${successLabel} 생성: 성공 ${successCount}개, 실패 ${failCount}개`);
        } else {
          message.error(`${successLabel} 생성에 실패했습니다.`);
        }
      }
    },
    [dispatch, me, startRankPolling],
  );

  const onCreate = useCallback(
    (dto: CreateSlotDTO): Promise<boolean> => {
      if (!me) return Promise.resolve(false);

      const tempId = createTempSlotId();
      const optimisticSlot = buildOptimisticSlot(dto, me, accounts, tempId);
      dispatch(optimisticAddSlots({ slots: [optimisticSlot] }));

      dispatch(createSlot({ dto, me, tempId, optimisticApplied: true }))
        .unwrap()
        .then((saved) => {
          message.success('슬롯이 생성되었습니다.');
          if (shouldKeepRankPending(saved)) {
            startRankPolling([saved.slotId]);
          }
        })
        .catch(() => {
          dispatch(removeOptimisticSlots({ tempIds: [tempId] }));
          message.error('슬롯 생성에 실패했습니다.');
        });

      return Promise.resolve(true);
    },
    [dispatch, me, accounts, startRankPolling],
  );

  const onExtend = useCallback(
    (days: number) => {
      if (!me) return;
      const ids = selected as number[];
      const snapshots = ids.map((id) => ({
        id,
        listSlot: cloneSlot(slotById.get(id)),
        defaultSlot: cloneSlot(defaultSlotById.get(id)),
      }));
      dispatch(optimisticBatchExtend({ ids, days }));
      dispatch(batchExtend({ ids, days, me, optimisticApplied: true }))
        .unwrap()
        .then(() => {
          handleSuccess('연장 완료되었습니다.');
        })
        .catch(() => {
          dispatch(rollbackSlots(snapshots));
          message.error('연장 실패하였습니다.');
        });
    },
    [dispatch, selected, me, handleSuccess, cloneSlot, slotById, defaultSlotById]);

  const onRefund = useCallback(() => {
    if (!me) return;
    const ids = selected as number[];
    const snapshots = ids.map((id) => ({
      id,
      listSlot: cloneSlot(slotById.get(id)),
      defaultSlot: cloneSlot(defaultSlotById.get(id)),
    }));
    dispatch(optimisticBatchRefund({ ids }));
    dispatch(batchRefund({ ids, me, optimisticApplied: true }))
      .unwrap()
      .then(() => {
        handleSuccess('환불 완료되었습니다.');
      })
      .catch(() => {
        dispatch(rollbackSlots(snapshots));
        message.error('환불 실패하였습니다.');
      });
  }, [dispatch, selected, me, handleSuccess, cloneSlot, slotById, defaultSlotById]);

  const onUpdate = useCallback(
    (id: number, dto: Partial<Slot>) => {
      if (!me) return Promise.resolve();
      const baseSlot = slotById.get(id) ?? defaultSlotById.get(id);
      const needsRankWatch = dtoTriggersAsyncRank(dto, baseSlot);
      const snapshots = [{
        id,
        listSlot: cloneSlot(slotById.get(id)),
        defaultSlot: cloneSlot(defaultSlotById.get(id)),
      }];

      // 저장값은 즉시 반영하고, 순위 계산은 서버 백그라운드 작업에 맡긴다.
      // 완료 버튼은 PATCH 성공 후에만 편집 상태를 닫아 실패를 성공처럼 보이지 않게 한다.
      dispatch(optimisticUpdateSlot({ id, dto }));
      return dispatch(updateSlot({ id, dto, me, optimisticApplied: true }))
        .unwrap()
        .then((saved) => {
          if (needsRankWatch) startRankPolling([saved.slotId]);
        })
        .catch((error) => {
          dispatch(rollbackSlots(snapshots));
          message.error('수정 실패하였습니다.');
          return Promise.reject(error);
        });
    },
    [dispatch, me, slotById, defaultSlotById, cloneSlot, startRankPolling]);

  // 슬롯들 모두 업데이트
  const onBulkUpdate = useCallback(
    async (dto: Partial<Slot>) => {
      if (!me) return;

      const ids = selected as number[];
      const totalCount = ids.length;
      const CHUNK_SIZE = 25;
      const THRESHOLD = 50;
      const DELAY_MS = 10000;
      const needsRankWatch = ids.some((id) =>
        dtoTriggersAsyncRank(dto, slotById.get(id) ?? defaultSlotById.get(id)),
      );

      if (totalCount <= THRESHOLD) {
        const results = await Promise.all(
          ids.map(id => dispatch(updateSlot({ id, dto, me })).unwrap().then(() => true).catch(() => false))
        );
        const okIds = ids.filter((_, index) => results[index]);
        const ok = okIds.length;
        const err = results.length - ok;
        if (ok && !err) message.success(`${ok}개 슬롯 수정 완료`);
        else if (ok && err) message.warning(`${ok}개 성공, ${err}개 실패`);
        else message.error('슬롯 수정 실패');
        if (needsRankWatch && okIds.length > 0) {
          beginAsyncRankWatch(okIds, { invalidateRank: true });
        }
        setSelected([]);
        return;
      }

      // 백그라운드 처리로 넘기고 모달 닫기를 위해 즉시 resolve 처리
      const runBulkUpdate = async () => {
        setIsBatchUploading(true);
        setBatchUploadTotal(totalCount);
        setBatchUploadCurrent(0);

        let successCount = 0;
        let failCount = 0;
        const okIds: number[] = [];

        const chunks: number[][] = [];
        for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
          chunks.push(ids.slice(i, i + CHUNK_SIZE));
        }

        try {
          for (let i = 0; i < chunks.length; i++) {
            const chunkIds = chunks[i];
            const results = await Promise.all(
              chunkIds.map(id => dispatch(updateSlot({ id, dto, me })).unwrap().then(() => true).catch(() => false))
            );
            results.forEach((ok, index) => {
              if (ok) okIds.push(chunkIds[index]);
            });
            const ok = results.filter(Boolean).length;
            successCount += ok;
            failCount += results.length - ok;
            setBatchUploadCurrent(successCount + failCount);

            if (i < chunks.length - 1) {
              await new Promise(resolve => setTimeout(resolve, DELAY_MS));
            }
          }
        } finally {
          setIsBatchUploading(false);
          setSelected([]);
          if (needsRankWatch && okIds.length > 0) {
            beginAsyncRankWatch(okIds, { invalidateRank: true });
          }
          if (failCount === 0) message.success(`총 ${successCount}개 슬롯 수정 완료`);
          else message.warning(`성공 ${successCount}개, 실패 ${failCount}개`);
        }
      };

      runBulkUpdate();
    },
    [dispatch, me, selected, slotById, defaultSlotById, beginAsyncRankWatch],
  );

  /* ---------------- 삭제 ---------------- */
  const onDelete = useCallback(() => {
    if (!me) return;
    try {
      assertAdminOrManager(me);
    } catch (e) {
      message.error((e as Error).message);
      return;
    }

    const ids = Array.from(new Set(selected as number[]));
    const snapshots = ids.map((id) => ({
      id,
      listSlot: cloneSlot(slotById.get(id) ?? defaultSlotById.get(id)),
      defaultSlot: cloneSlot(defaultSlotById.get(id) ?? slotById.get(id)),
    }));

    dispatch(optimisticDeleteSlots({ ids }));
    handleSuccess(`${ids.length}개 슬롯이 삭제되었습니다.`);

    void Promise.all(
      ids.map((id) =>
        dispatch(deleteSlot({ id, me, optimisticApplied: true }))
          .unwrap()
          .then(() => true)
          .catch(() => false),
      ),
    ).then((results) => {
      const failedSnapshots = snapshots.filter((_, index) => !results[index]);
      if (failedSnapshots.length === 0) return;

      dispatch(rollbackSlots(failedSnapshots));
      const successCount = ids.length - failedSnapshots.length;
      if (successCount > 0) {
        message.warning(`${failedSnapshots.length}개 슬롯 삭제에 실패하여 복구했습니다.`);
      } else {
        message.error('슬롯 삭제에 실패했습니다.');
      }
    });
  }, [me, selected, dispatch, handleSuccess, cloneSlot, slotById, defaultSlotById]);

  /* ---------------- 일괄 생성 ---------------- */
  const onBatchCreate = useCallback(
    async (request: { count: number; slots: CreateSlotDTO[] }) => {
      if (!me) return;

      const { tempIds, slots: optimisticSlots } = buildOptimisticSlotsFromRequest(
        request,
        me,
        accounts,
        displaySlots.length,
      );

      dispatch(optimisticAddSlots({ slots: optimisticSlots }));
      message.info(`${request.count}개 슬롯을 목록에 추가했습니다. 순위 확인 중…`);

      void runBackgroundBatchCreate(request, tempIds, {
        successLabel: '슬롯',
      });
    },
    [me, accounts, displaySlots.length, dispatch, runBackgroundBatchCreate],
  );

  /* ---------------- 엑셀 다운로드 ---------------- */
  const [excelDownloading, setExcelDownloading] = useState(false);
  
  const handleExcelDownload = useCallback(async () => {
    if (!me) return;
    
    setExcelDownloading(true);
    
    try {
      let slotsToExport: Slot[];

      if (selected.length > 0) {
        // 체크된 슬롯이 있으면 선택된 것들만 다운로드
        slotsToExport = selectedSlots;
      } else {
        // 선택된 슬롯이 없으면 전체 조회 (active=true)
        const response = await dispatch(fetchAllSlots({ me, active: true })).unwrap();
        slotsToExport = response.slots;
      }

      if (slotsToExport.length === 0) {
        message.warning('다운로드할 슬롯이 없습니다.');
        return;
      }

      // 클라이언트 사이드에서 엑셀 생성
      const { exportSlotsToExcel } = await import('../../Utils/exportSlotsToExcel');
      const isAllDownload = selected.length === 0; // 선택된 슬롯이 없으면 전체 다운로드
      exportSlotsToExcel(slotsToExport, accounts, isAllDownload);
      message.success(`${slotsToExport.length}개 슬롯이 다운로드되었습니다.`);
    } catch (error) {
      message.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setExcelDownloading(false);
    }
  }, [dispatch, me, accounts, selectedSlots, selected]);

  const handleExcelDownloadWithLatestRanks = useCallback(async () => {
    if (!me || !isAdpa99Username(me.username)) return;

    const slotsToExport = selected.length > 0 ? selectedSlots : displaySlots;
    if (slotsToExport.length === 0) {
      message.warning('다운로드할 슬롯이 없습니다.');
      return;
    }

    try {
      const { exportSlotsToExcel } = await import('../../Utils/exportSlotsToExcel');
      exportSlotsToExcel(slotsToExport, accounts, selected.length === 0, { includeTodayRank: true });
      message.success(`${slotsToExport.length}개 슬롯이 다운로드되었습니다.`);
    } catch (error) {
      message.error('엑셀 다운로드에 실패했습니다.');
    }
  }, [me, accounts, selected, selectedSlots, displaySlots]);


  // 엑셀 업로드(사전 파싱 경로): 파일만 받아 모달을 연다
  const openExcelImport = useCallback((file: File) => {
    setExcelImportFile(file);
    setExcelImportOpen(true);
  }, []);

  const openExcelBatchUpdate = useCallback((file: File) => {
    setExcelBatchUpdateFile(file);
    setExcelBatchUpdateOpen(true);
  }, []);

  const handleExcelImportSubmit = useCallback(async (request: { count: number; slots: CreateSlotDTO[] }) => {
    if (!me) return;

    setExcelImportOpen(false);
    setExcelImportFile(null);

    const allSlots = request.slots;
    const totalCount = allSlots.length;
    const batchRequest = { count: 1, slots: allSlots };
    const { tempIds, slots: optimisticSlots } = buildOptimisticSlotsFromRequest(
      batchRequest,
      me,
      accounts,
      displaySlots.length,
    );

    dispatch(optimisticAddSlots({ slots: optimisticSlots }));
    message.info(`${totalCount}개 슬롯을 목록에 추가했습니다. 순위 확인 중…`);

    void runBackgroundBatchCreate(batchRequest, tempIds, {
      successLabel: '슬롯 업로드',
    });
  }, [me, accounts, displaySlots.length, dispatch, runBackgroundBatchCreate]);

  /* ==============  에러 알림 ============== */
  useEffect(() => { if (error) message.error(error); }, [error]);

  /* ==============  아직 사용자 정보가 없으면 스피너 ============== */
  if (!me) {
    return <Spin style={{ display: 'block', margin: '160px auto', color: '#B03052' }} />;
  }

  /* ==============  JSX  ============== */
  return (
    <div ref={topRef}>
      <Card title="슬롯 관리" bodyStyle={{ padding: 0 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ padding: '16px 16px 0 16px' }}>
            <SlotOpsOverrideBanner />
          </div>
          {isBatchUploading && (
            <div style={{ padding: '16px 16px 0 16px' }}>
              <Alert
                message="서버 동기화 진행 중 (25개씩 분할)"
                description={
                  <div style={{ marginTop: 8 }}>
                    <Text>
                      목록에는 이미 반영되었습니다. 서버 저장 {batchUploadCurrent}/{batchUploadTotal}개 완료
                      (최대 {BATCH_CREATE_CHUNK_SIZE}개씩 요청)
                    </Text>
                    <Progress 
                      percent={Math.floor((batchUploadCurrent / batchUploadTotal) * 100)} 
                      status="active" 
                      strokeColor="#B03052"
                    />
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                      * 순위는 저장 직후 「순위 확인 중」으로 표시되며, 잠시 후 자동 갱신됩니다. 창을 닫거나 새로고침하면 남은 동기화가 중단될 수 있습니다.
                    </Text>
                  </div>
                }
                type="info"
                showIcon
              />
            </div>
          )}
          <ManagementBar
            /* 선택 & 액션 */
            selected={selected}
            selectedSlots={selectedSlots}
            onAdd={() => setAddOpen(true)}
            onExtend={() => setExtOpen(true)}
            onEdit={() => setEditOpen(true)}
            onRefund={() => setRefundOpen(true)}
            onDelete={onDelete}
            /* 검색 / 상태 */
            search={search}
            onSearchChange={setSearch}
            onSearch={handleSearch}
            status=""
            onStatusChange={() => {}}
            searchField={searchField}
            onSearchFieldChange={handleSearchFieldChange}
            onBatchEdit={() => setBulkEditOpen(true)}
            /* 정렬 */
            sortBy={searchParams.order_by || 'registered'}
            sortDir={searchParams.order_dir || 'desc'}
            onSortByChange={(v) => updateSearchParams({ order_by: v })}
            onSortDirChange={(v) => updateSearchParams({ order_dir: v })}
            /* 엑셀 */
            onExcelDownload={handleExcelDownload}
            onExcelDownloadWithLatestRanks={handleExcelDownloadWithLatestRanks}
            onExcelParsedUpload={openExcelImport}
            onExcelBatchUpdateUpload={openExcelBatchUpdate}
            excelUploading={excelUploading}
            excelDownloading={excelDownloading}
            localFindOpen={localFindOpen}
            localFindQuery={localFindQuery}
            localFindMatchCount={localFindMatchCount}
            localFindActiveMatchIndex={localFindActiveMatchIndex}
            localFindInputRef={localFindInputRef}
            onOpenLocalFind={openLocalFind}
            onCloseLocalFind={closeLocalFind}
            onLocalFindQueryChange={handleLocalFindQueryChange}
            onLocalFindPrev={handleLocalFindPrev}
            onLocalFindNext={handleLocalFindNext}
          />

          <SlotTable
            data={displaySlots}
            loading={loading}
            selected={selected}
            onSelectChange={handleSelectChange}
            onInlineUpdate={onUpdate}
            pagination={effectivePagination || undefined}
            onPageChange={handlePageChange}
            localFindQuery={localFindQuery}
            activeLocalFindMatchIndex={localFindActiveMatchIndex}
            onLocalFindMatchCountChange={handleLocalFindMatchCountChange}
            onLocalFindScrollApplied={restoreLocalFindInputFocus}
          />
        </Space>

        {/* ---------- 모달 ---------- */}
        <SlotAddModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onSubmit={onCreate}
          onBatchSubmit={onBatchCreate}
        />

        <SlotExtendModal
          open={extOpen}
          onClose={() => setExtOpen(false)}
          onSubmit={onExtend}
        />

        <SlotEditModal
          open={editOpen}
          slotIds={selected as number[]}
          onClose={() => setEditOpen(false)}
          onSubmit={dto => Promise.all(
            (selected as number[]).map(id => onUpdate(id, dto)),
          ).then(() => undefined)}
        />

        <SlotRefundModal
          open={refundOpen}
          onClose={() => setRefundOpen(false)}
          onSubmit={onRefund}
        />

        <SlotBulkEditModal
          open={bulkEditOpen}
          slotCount={selected.length}
          onClose={() => setBulkEditOpen(false)}
          onSubmit={onBulkUpdate}
        />
          <ExcelImportModal
          open={excelImportOpen}
          file={excelImportFile}
          onClose={() => { setExcelImportOpen(false); setExcelImportFile(null); }}
          onSubmit={handleExcelImportSubmit}
        />
        <ExcelBatchUpdateModal
          open={excelBatchUpdateOpen}
          file={excelBatchUpdateFile}
          onClose={() => { setExcelBatchUpdateOpen(false); setExcelBatchUpdateFile(null); }}
          onSuccess={(rankPollIds) => {
            setExcelBatchUpdateOpen(false);
            setExcelBatchUpdateFile(null);
            if (rankPollIds && rankPollIds.length > 0) {
              beginAsyncRankWatch(rankPollIds, { invalidateRank: true });
            }
            // batchUpdateSlots.fulfilled → needsRefresh; useSlots effect에서 fetchSlots 1회
          }}
        />
      </Card>
    </div>
  );
};

export default SlotsPage;